import { useEffect, useState } from 'react';

import { getAccountDeletionsRequest } from 'api';

/** Who asked for the closure. Mirrors `closedBy.actor` on the API. */
export type ClosedByActor = 'self' | 'admin';

/**
 * Who closed a deleted account: the person themselves, or an administrator.
 *
 * ## Why this is a request and not a field on the profile
 *
 * `GET /users/:id` cannot answer it. Deletion anonymises the `user` row in
 * place, and the row that records WHO asked lives in `account_deletion`, which
 * only `GET /admin/account-deletions` reads. So the one screen that has to say
 * the sentence is the one screen with none of the evidence for it.
 *
 * ## Why it is worth a request at all
 *
 * Both detail pages used to tell an operator, flatly, that a closed account
 * "was closed by the person who owned it". Now that administrators can close
 * one themselves that sentence is false for some rows -- and it is the kind of
 * false that ends a support conversation in the wrong place, because "they say
 * they never asked to be deleted" is answered by this line and by nothing else
 * on the page.
 *
 * Nothing is fetched unless the account is actually closed, so an ordinary
 * profile costs nothing.
 *
 * Returns `null` while the answer is unknown -- still in flight, no record
 * found, or the request failed -- and the caller says something that is true
 * either way rather than guessing. `q` takes a user id verbatim (the API
 * matches a UUID against `userId` exactly), and `outcome` keeps refusals out:
 * somebody can be refused on Monday and closed on Friday, and only the closure
 * describes the state the page is showing.
 */
export const useAccountClosure = (
  userId?: string,
  isClosed?: boolean,
): ClosedByActor | null => {
  const [actor, setActor] = useState<ClosedByActor | null>(null);

  useEffect(() => {
    if (!userId || !isClosed) {
      setActor(null);
      return undefined;
    }

    // Guards against the answer for a previous profile landing on this one --
    // the pages are navigated between from a list, and the request outlives the
    // route change.
    let cancelled = false;

    getAccountDeletionsRequest({ q: userId, outcome: 'deleted', limit: 1 })
      .then(res => {
        if (cancelled) return;

        const entry = res.data?.entries?.[0];
        if (!entry) {
          setActor(null);
          return;
        }

        setActor(entry.closedBy?.actor === 'admin' ? 'admin' : 'self');
      })
      .catch(() => {
        if (!cancelled) setActor(null);
      });

    return () => {
      cancelled = true;
    };
  }, [userId, isClosed]);

  return actor;
};
